import { UpperCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { PerioFormControlService } from '../../services/perio-form-control/perio-form-control.service';

type NumberDisplayType = 'arabic' | 'roman';
type NumberSelectorVariant = 'blue' | 'orange';
type MeasurementType = 'furcuation' | 'mobility';
interface TranslationStringMap {
  name: string;
  label: string;
}

@Component({
  selector: 'lib-perio-number-selector',
  imports: [UpperCasePipe, TranslatePipe],
  templateUrl: './perio-number-selector.component.html',
  styleUrl: './perio-number-selector.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PerioNumberSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class PerioNumberSelectorComponent implements ControlValueAccessor, AfterViewInit {
  private readonly elementRef = inject(ElementRef);
  private readonly formControlService = inject(PerioFormControlService);
  private readonly destroyRef = inject(DestroyRef);

  public numberDisplayType = input<NumberDisplayType>('arabic');
  public measurementType = input.required<MeasurementType>();
  public variant = input.required<NumberSelectorVariant>();
  public disabled = input(false);
  public reversed = input<boolean>(false);

  public valueChange = output<number>();

  @HostBinding('attr.tabindex')
  public tabindex = -1;

  @HostListener('focusin')
  @HostListener('focus')
  public handleFocus(): void {
    this.focused.set(true);
  }

  @HostListener('focusout')
  @HostListener('blur')
  public handleBlur(): void {
    this.focused.set(false);
  }

  @HostListener('keydown', ['$event'])
  public handleKeyStroke(e: KeyboardEvent): void {
    e.preventDefault();
    if (!this.disabled() && /\d/.test(e.key) && this.valueSet.includes(+e.key)) {
      this.writeValue(+e.key);
      this.formControlService.focusActivePocketDepth(this.reversed());
    }
  }

  protected readonly name = computed(() => this.measurementTypeTranslationKeyMap[this.measurementType()].name);
  protected readonly label = computed(() => this.measurementTypeTranslationKeyMap[this.measurementType()].label);
  protected readonly selected = signal<number | null>(null);
  protected readonly focused = signal<boolean>(false);

  protected valueSet: number[] = [...Array.from({ length: 5 }).keys()];
  protected romanNumerals: string[] = ['0', 'I', 'II', 'III', 'IV'];

  protected titleClass = computed(() => {
    if (this.disabled()) {
      return 'text-states-grey';
    }
    return this.variant() === 'blue' ? 'text-states-blue' : 'text-states-orange';
  });

  protected measurementTypeTranslationKeyMap: Record<MeasurementType, TranslationStringMap> = {
    mobility: {
      name: 'perio_exam_mobility_acronym',
      label: 'perio_exam_mobility_label',
    },
    furcuation: {
      name: 'perio_exam_furcuation_acronym',
      label: 'perio_exam_furcuation_label',
    },
  };

  private onChange: (val: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly commonButtonClassList = ['flex', 'h-8', 'w-8', 'items-center', 'justify-center', 'rounded-full', 'border', 'font-thin'];
  private readonly colorButtonClassList = ['border-states-light-grey', 'bg-states-white', 'text-states-light-white'];
  private readonly activeColorButtonClassList = ['bg-states-primary-blue', 'text-states-white'];
  private readonly activeFocusedButtonClassList = [
    'bg-states-primary-blue',
    'text-states-white',
    'border-states-white',
    'border-2',
    'outline',
    'outline-states-primary-blue',
    'outline-[2px]',
    'mr-[2px]',
  ];

  public ngAfterViewInit(): void {
    const focusListener$ =
      this.measurementType() === 'mobility'
        ? this.formControlService.getMobilityTrigger$()
        : this.formControlService.getFurcuationTrigger$();

    focusListener$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.focus();
    });

    // Listen for mobility value changes from voice commands
    if (this.measurementType() === 'mobility') {
      this.formControlService
        .getMobilityValue$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.writeValue(value);
          this.onTouched();
        });
    }

    // Listen for furcation value changes from voice commands
    if (this.measurementType() === 'furcuation') {
      this.formControlService
        .getFurcuationValue$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.writeValue(value);
          this.onTouched();
        });
    }
  }

  public writeValue(value: number | null): void {
    this.selected.set(value);
    this.onChange(value);
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected onButtonClick(val: number): void {
    if (this.disabled()) {
      return;
    }
    this.writeValue(val);
    this.onTouched();
    this.formControlService.focusActivePocketDepth();
  }

  public focus(): void {
    this.elementRef.nativeElement.focus();
  }

  protected getButtonClassList(value: number): string[] {
    if (this.disabled()) {
      return [
        ...this.commonButtonClassList,
        'border-states-light-grey',
        'bg-states-light-grey',
        'text-states-white',
        'pointer-events-none',
      ];
    }

    if (value !== this.selected()) {
      return [...this.commonButtonClassList, ...this.colorButtonClassList];
    }

    if (!this.focused()) {
      return [...this.commonButtonClassList, ...this.activeColorButtonClassList];
    }

    return [...this.commonButtonClassList, ...this.activeFocusedButtonClassList];
  }
}
